import { create } from 'zustand';
import type { FlowEmail, FlowSummary } from '@/lib/api/email-flows/types';
import type { EmailFormData, TemplateVariables } from '../types';

interface EmailEditorStore {
    // Flow and email data
    flowData: FlowSummary | null;
    emailData: FlowEmail | null;

    // Template data
    templateVariables: TemplateVariables | null;
    templateDefaults: EmailFormData | null;
    defaultValues: EmailFormData | null;

    // Metadata (computed from flow/email data)
    trigger: string;
    isFirstTimeBasedEmail: boolean;
    isCartAbandonedFlow: boolean;
    actionsDisabled: boolean;
    hasSentEmails: boolean;
    triggerEventName: string;
    canAddAnotherEmail: boolean;

    // Current form values for test send
    subject: string;
    body: string;

    // Actions
    setFlowData: (flowData: FlowSummary) => void;
    setEmailData: (emailData: FlowEmail | null) => void;
    setTemplateData: (variables: TemplateVariables, defaults: EmailFormData, defaultValues: EmailFormData) => void;
    setMetadata: (metadata: {
        trigger: string;
        isFirstTimeBasedEmail: boolean;
        isCartAbandonedFlow: boolean;
        actionsDisabled: boolean;
        hasSentEmails: boolean;
        triggerEventName: string;
        canAddAnotherEmail: boolean;
    }) => void;
    setSubject: (subject: string) => void;
    setBody: (body: string) => void;
    reset: () => void;
}

const initialState = {
    flowData: null,
    emailData: null,
    templateVariables: null,
    templateDefaults: null,
    defaultValues: null,
    trigger: 'checkout',
    isFirstTimeBasedEmail: false,
    isCartAbandonedFlow: false,
    actionsDisabled: false,
    hasSentEmails: false,
    triggerEventName: '',
    canAddAnotherEmail: true,
    subject: '',
    body: '',
};

export const useEmailEditorStore = create<EmailEditorStore>(set => ({
    ...initialState,

    setFlowData: (flowData: FlowSummary) => set({ flowData }),

    setEmailData: (emailData: FlowEmail | null) => set({ emailData }),

    setTemplateData: (variables, defaults, defaultValues) =>
        set({
            templateVariables: variables,
            templateDefaults: defaults,
            defaultValues,
        }),

    setMetadata: metadata => set(metadata),

    setSubject: (subject: string) => set({ subject }),

    setBody: (body: string) => set({ body }),

    reset: () => set(initialState),
}));
